import { useState, useEffect } from 'react';
import { useTheme } from './theme';
import { db } from './dbClient';
import { useToast } from './Toast';
import AuditLogPanel from './AuditLogPanel';
import { useAuditLog } from './useAuditLog';
import CaseDetail from './CaseDetail';

const DISPOSITIONS = [
  { value: 'approved', label: '✓ Approved', color: '#4CAF50' },
  { value: 'needs_revision', label: '⚠️ Needs Revision', color: '#FFA500' },
  { value: 'escalate', label: '🚨 Escalate', color: '#FF6B6B' },
  { value: 'return_to_analyst', label: '↩️ Return to Analyst', color: '#FF9800' },
];

export default function QADashboard({ company, currentUser, team = [] }) {
  const C = useTheme();
  const { showToast } = useToast();
  const { log } = useAuditLog(company, currentUser);

  const [view, setView] = useState('queue'); // queue, review, metrics, config
  const [qaQueue, setQaQueue] = useState([]);
  const [selectedQA, setSelectedQA] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  // Review form state
  const [reviewData, setReviewData] = useState({
    disposition: 'approved',
    analyst_score: 85,
    analyst_feedback: '',
    reviewer_feedback: '',
    issues_found: [],
    recommended_actions: '',
    scoring_rubric: {
      disposition: 85,
      conclusion: 85,
      narrative_format: 85,
      reasonability: 85,
      compliance: 85,
    },
  });

  useEffect(() => {
    if (company?.id) {
      loadQAQueue();
      loadMetrics();
      loadConfig();
    }
  }, [company?.id]);

  async function loadQAQueue() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('qa_queue')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('[QA] Queue loaded:', (data || []).map(q => ({ id: q.id, status: q.status, case_id: q.case_id })));
      setQaQueue(data || []);
    } catch (err) {
      showToast('Error loading QA queue', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMetrics() {
    if (!company?.id) return;
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      // Get QA queue metrics
      const { data: queueData, error: queueErr } = await db
        .from('qa_queue')
        .select('*')
        .eq('company_id', company.id);

      if (queueErr) throw queueErr;

      const pendingCount = queueData?.filter(q => q.status === 'pending').length || 0;
      const overdueCount = queueData?.filter(q =>
        q.due_date && new Date(q.due_date) < new Date() && q.status !== 'completed'
      ).length || 0;

      // Get QA review metrics (last 100 reviews)
      const { data: reviewData, error: reviewErr } = await db
        .from('qa_reviews')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (reviewErr) throw reviewErr;

      // Filter to 30 days on client side
      const recentReviews = reviewData?.filter(r => new Date(r.created_at) >= new Date(thirtyDaysAgo)) || [];
      const totalReviewed = recentReviews.length;
      const approved = recentReviews.filter(r => r.disposition === 'approved').length;
      const needsRevision = recentReviews.filter(r => r.disposition === 'needs_revision').length;
      const escalated = recentReviews.filter(r => r.disposition === 'escalate').length;
      const avgAnalystScore = recentReviews.length ? Math.round(
        recentReviews.reduce((sum, r) => sum + (r.analyst_score || 0), 0) / recentReviews.length
      ) : 0;

      setMetrics({
        pending_count: pendingCount,
        overdue_count: overdueCount,
        total_reviewed: totalReviewed,
        approved,
        needs_revision: needsRevision,
        escalated,
        avg_analyst_score: avgAnalystScore,
      });
    } catch (err) {
      console.error('Error loading QA metrics:', err);
    }
  }

  async function loadConfig() {
    if (!company?.id) return;
    try {
      const { data, error } = await db
        .from('qa_sampling_config')
        .select('*')
        .eq('company_id', company.id)
        .limit(1);

      if (error) throw error;
      setConfig(data?.[0] || null);
    } catch (err) {
      console.error('Error loading QA config:', err);
    }
  }

  async function handleReviewSubmit() {
    if (!selectedQA?.id) {
      showToast('No QA item selected', 'error');
      return;
    }

    try {
      // Insert review
      const { data: reviewRes, error: reviewErr } = await db
        .from('qa_reviews')
        .insert([{
          company_id: company.id,
          qa_queue_id: selectedQA.id,
          case_id: selectedQA.case_id,
          reviewer_id: currentUser.id,
          ...reviewData,
        }]);

      if (reviewErr) throw reviewErr;

      // Update QA queue status
      const { error: updateErr } = await db
        .from('qa_queue')
        .update({ status: 'completed' })
        .eq('id', selectedQA.id);

      if (updateErr) throw updateErr;

      // Log action
      await log({
        action: 'qa_review_submitted',
        module: 'qa_dashboard',
        entityId: selectedQA.id,
        entityName: selectedQA.case_id,
        newValue: { disposition: reviewData.disposition, score: reviewData.analyst_score },
      });

      showToast('Review submitted successfully', 'success');
      setSelectedQA(null);
      setShowForm(false);
      setReviewData({
        disposition: 'approved',
        analyst_score: 85,
        analyst_feedback: '',
        reviewer_feedback: '',
        issues_found: [],
        recommended_actions: '',
        scoring_rubric: {
          disposition: 85,
          conclusion: 85,
          narrative_format: 85,
          reasonability: 85,
          compliance: 85,
        },
      });
      loadQAQueue();
      loadMetrics();
    } catch (err) {
      showToast('Error submitting review', 'error');
      console.error(err);
    }
  }

  async function handleManualSample() {
    // Allow selecting a closed case to manually add to QA queue
    const caseId = prompt('Enter Case ID to sample manually:');
    console.log('[QA] Manual sample prompt result:', caseId);
    if (!caseId) return;

    try {
      console.log('[QA] Checking if case already exists:', { company_id: company.id, case_id: caseId });

      // Check if case is already in QA queue
      const existing = await db
        .from('qa_queue')
        .select('id')
        .eq('company_id', company.id)
        .eq('case_id', caseId)
        .maybeSingle();

      console.log('[QA] Check result - data:', existing?.data, 'error:', existing?.error);

      if (existing.data) {
        showToast('This case is already in the QA queue', 'info');
        return;
      }

      if (existing.error && !existing.error.message.includes('No rows')) {
        throw existing.error;
      }

      console.log('[QA] Inserting case into queue:', { company_id: company.id, case_id: caseId });

      const { error } = await db
        .from('qa_queue')
        .insert([{
          company_id: company.id,
          case_id: caseId,
          sampling_type: 'manual',
          sample_reason: 'Manually selected for QA review',
          due_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        }]);

      console.log('[QA] Insert result:', { error });

      if (error) throw error;
      showToast('Case added to QA queue', 'success');
      console.log('[QA] Case added successfully, reloading queue...');
      await loadQAQueue();
      console.log('[QA] Queue reloaded, total items:', qaQueue.length);
    } catch (err) {
      const errorMsg = err?.message?.includes('duplicate')
        ? 'This case is already in the QA queue'
        : 'Error adding case to QA queue';
      showToast(errorMsg, 'error');
      console.error('[QA] Error:', err);
    }
  }

  async function handleAssignQA(qaId, userId) {
    try {
      const { error } = await db
        .from('qa_queue')
        .update({ assigned_to: userId, status: 'in_review' })
        .eq('id', qaId);

      if (error) throw error;
      showToast('QA item assigned', 'success');
      loadQAQueue();
    } catch (err) {
      showToast('Error assigning QA item', 'error');
      console.error(err);
    }
  }

  async function handleViewReview(qaItem) {
    try {
      console.log('[QA-DEBUG] handleViewReview called with qaItem:', { id: qaItem.id, case_id: qaItem.case_id, status: qaItem.status });

      const { data: reviewData, error: reviewErr } = await db
        .from('qa_reviews')
        .select('*')
        .eq('qa_queue_id', qaItem.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (reviewErr) throw reviewErr;

      console.log('[QA-DEBUG] Loaded review data:', reviewData);

      // Use API endpoint instead of Supabase client for case loading
      const token = localStorage.getItem('amlguard_token');
      const caseResponse = await fetch(`/api/query?resource=cases&filter=${JSON.stringify({ id: qaItem.case_id })}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const caseDataArray = await caseResponse.json();
      const caseData = Array.isArray(caseDataArray) ? caseDataArray[0] : caseDataArray.cases?.[0];

      console.log('[QA-DEBUG] Loaded case data:', { id: caseData?.id, entity: caseData?.entity });

      const review = reviewData && reviewData.length > 0
        ? { ...reviewData[0], caseDetails: caseData }
        : { caseDetails: caseData };

      console.log('[QA-DEBUG] Setting selectedReview with case:', { id: review.caseDetails?.id, entity: review.caseDetails?.entity });

      setSelectedReview(review);
      setSelectedQA(qaItem);
      setShowDetail(true);
    } catch (err) {
      showToast('Error loading review details', 'error');
      console.error('[QA-DEBUG] Error:', err);
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: C.textDim,
      in_review: '#FFA500',
      completed: '#4CAF50',
      approved: '#4CAF50',
      needs_revision: '#FF9800',
      escalate: '#FF6B6B',
    };
    return colors[status] || C.textMid;
  };

  const getRiskColor = (risk) => {
    const colors = {
      CRITICAL: '#FF6B6B',
      HIGH: '#FF9800',
      MEDIUM: '#FFA500',
      LOW: '#4CAF50',
    };
    return colors[risk] || C.textMid;
  };

  const pendingCount = qaQueue.filter(qa => qa.status === 'pending').length;
  const overdueCount = qaQueue.filter(qa => qa.due_date && new Date(qa.due_date) < new Date() && qa.status !== 'completed').length;

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading QA Dashboard...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          Quality Control (QA) Dashboard
        </h1>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Review and audit closed cases for accuracy and consistency.
        </p>
      </div>

      {/* Metrics overview */}
      {metrics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <MetricCard label="Pending Reviews" value={metrics.pending_count} color={C.accent} />
          <MetricCard label="Overdue" value={metrics.overdue_count} color="#FF6B6B" />
          <MetricCard label="Reviewed (30d)" value={metrics.total_reviewed} color={C.steel} />
          <MetricCard label="Approved" value={metrics.approved} color="#4CAF50" />
          <MetricCard label="Avg Score" value={`${metrics.avg_analyst_score || 0}%`} color={C.gold} />
        </div>
      )}

      {/* View switcher */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { value: 'queue', label: `📋 QA Queue (${pendingCount})` },
          { value: 'metrics', label: '📊 Metrics' },
          { value: 'config', label: '⚙️ Configuration' },
        ].map(v => (
          <button
            key={v.value}
            onClick={() => setView(v.value)}
            style={{
              padding: '0.6rem 1rem',
              background: view === v.value ? C.accent : 'transparent',
              color: view === v.value ? '#0A0E1A' : C.text,
              border: `1px solid ${view === v.value ? C.accent : C.border}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontWeight: view === v.value ? 700 : 400,
              fontSize: '0.85rem'
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* QA Queue view */}
      {view === 'queue' && (
        <div>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleManualSample}
              style={{
                padding: '0.6rem 1rem',
                background: `${C.accent}22`,
                color: C.accent,
                border: `1px solid ${C.accent}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontWeight: 700,
                fontSize: '0.85rem'
              }}
            >
              + Manually Sample Case
            </button>
          </div>

          {qaQueue.length === 0 ? (
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '2rem',
              textAlign: 'center',
              color: C.textDim,
            }}>
              No cases in QA queue. Configure QA sampling to get started.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {qaQueue.map(qa => (
                <div
                  key={qa.id}
                  onClick={() => {
                    handleViewReview(qa);
                    if (qa.status === 'pending' || qa.status === 'in_review') {
                      setShowForm(true);
                    } else if (qa.status === 'completed') {
                      setShowForm(false);
                    }
                  }}
                  style={{
                    background: selectedQA?.id === qa.id ? `${C.accent}11` : C.surface,
                    border: selectedQA?.id === qa.id ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                        {qa.cases?.entity}
                      </div>
                      <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {qa.cases?.type} • {new Date(qa.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span style={{
                        background: getRiskColor(qa.cases?.risk) + '22',
                        color: getRiskColor(qa.cases?.risk),
                        padding: '0.3rem 0.7rem',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        fontWeight: 700
                      }}>
                        {qa.cases?.risk}
                      </span>
                      <span style={{
                        background: getStatusColor(qa.status) + '22',
                        color: getStatusColor(qa.status),
                        padding: '0.3rem 0.7rem',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'uppercase'
                      }}>
                        {qa.status}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      CASE ID
                    </div>
                    <div style={{ color: C.text, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {qa.case_id || '—'}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                    <div>
                      <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                        SAMPLING TYPE
                      </div>
                      <div style={{ color: C.text }}>
                        {qa.sampling_type === 'random' ? '🎲 Random' : '👤 Manual'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                        DUE DATE
                      </div>
                      <div style={{
                        color: qa.due_date && new Date(qa.due_date) < new Date() ? '#FF6B6B' : C.text,
                        fontWeight: qa.due_date && new Date(qa.due_date) < new Date() ? 700 : 400
                      }}>
                        {qa.due_date ? new Date(qa.due_date).toLocaleDateString() : '—'}
                      </div>
                    </div>
                  </div>

                  {qa.status === 'pending' && (
                    <select
                      value={qa.assigned_to || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAssignQA(qa.id, e.target.value || null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 4,
                        color: C.text,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: '0.85rem'
                      }}
                    >
                      <option value="">Assign Reviewer...</option>
                      {team.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.firstName || t.first_name} {t.lastName || t.last_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review form (when QA selected) */}
      {view === 'queue' && selectedQA && showForm && (
        <QAReviewForm
          qa={selectedQA}
          reviewData={reviewData}
          setReviewData={setReviewData}
          onSubmit={handleReviewSubmit}
          onCancel={() => {
            setShowForm(false);
            setSelectedQA(null);
          }}
        />
      )}

      {/* Review detail (when completed QA selected) */}
      {view === 'queue' && selectedQA && showDetail && selectedReview && (
        <QAReviewDetail
          qa={selectedQA}
          review={selectedReview}
          company={company}
          currentUser={currentUser}
          team={team}
          onClose={() => {
            setShowDetail(false);
            setSelectedQA(null);
            setSelectedReview(null);
          }}
        />
      )}

      {/* Metrics view */}
      {view === 'metrics' && metrics && (
        <div style={{ display: 'grid', gap: '2rem' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1.5rem' }}>
            <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>30-Day Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', fontSize: '0.9rem' }}>
              {[
                { label: 'Total Reviewed', value: metrics.total_reviewed },
                { label: 'Approved', value: metrics.approved },
                { label: 'Needs Revision', value: metrics.needs_revision },
                { label: 'Escalated', value: metrics.escalated },
              ].map(m => (
                <div key={m.label} style={{ padding: '1rem', background: C.bg, borderRadius: 6 }}>
                  <div style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {m.label.toUpperCase()}
                  </div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: '1.5rem' }}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Config view */}
      {view === 'config' && (
        <QAConfigPanel company={company} config={config} onUpdate={loadConfig} />
      )}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  const C = useTheme();
  return (
    <div style={{
      background: `${color}11`,
      border: `1px solid ${color}33`,
      borderRadius: 8,
      padding: '1rem',
      textAlign: 'center'
    }}>
      <div style={{ color: C.textDim, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
        {label}
      </div>
      <div style={{ color: color, fontWeight: 700, fontSize: '1.8rem' }}>
        {value}
      </div>
    </div>
  );
}

function QAReviewForm({ qa, reviewData, setReviewData, onSubmit, onCancel }) {
  const C = useTheme();

  const rubricItems = [
    { key: 'disposition', label: 'Disposition', weight: 0.20 },
    { key: 'conclusion', label: 'Conclusion', weight: 0.20 },
    { key: 'narrative_format', label: 'Narrative Format', weight: 0.20 },
    { key: 'reasonability', label: 'Reasonability', weight: 0.20 },
    { key: 'compliance', label: 'Compliance', weight: 0.20 },
  ];

  const calculateOverallScore = () => {
    const rubric = reviewData.scoring_rubric || {};
    const total = rubricItems.reduce((sum, item) => {
      return sum + ((rubric[item.key] || 0) * item.weight);
    }, 0);
    return Math.round(total);
  };

  const updateRubricItem = (key, value) => {
    const newRubric = { ...reviewData.scoring_rubric, [key]: parseInt(value) };
    const newScore = Math.round(rubricItems.reduce((sum, item) => {
      return sum + ((newRubric[item.key] || 0) * item.weight);
    }, 0));
    setReviewData({
      ...reviewData,
      scoring_rubric: newRubric,
      analyst_score: newScore
    });
  };

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '1.5rem',
      marginTop: '2rem',
      maxHeight: '75vh',
      overflowY: 'auto'
    }}>
      <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
        QA Review: {qa.cases?.entity}
      </h3>

      <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.75rem' }}>
            Scoring Rubric (0-100 for each category) *
          </label>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
            {rubricItems.map(item => (
              <div key={item.key} style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: '0.75rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ color: C.text, fontSize: '0.85rem', fontWeight: 600 }}>
                    {item.label} ({Math.round(item.weight * 100)}%)
                  </label>
                  <div style={{ color: C.accent, fontWeight: 700, fontSize: '0.9rem' }}>
                    {reviewData.scoring_rubric?.[item.key] || 0}
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={reviewData.scoring_rubric?.[item.key] || 0}
                  onChange={(e) => updateRubricItem(item.key, e.target.value)}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
              </div>
            ))}
          </div>

          <div style={{
            background: C.accent + '22',
            border: `1px solid ${C.accent}`,
            borderRadius: 6,
            padding: '0.75rem',
            textAlign: 'center'
          }}>
            <div style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              CALCULATED OVERALL SCORE
            </div>
            <div style={{ color: C.accent, fontWeight: 700, fontSize: '1.5rem' }}>
              {calculateOverallScore()}%
            </div>
          </div>
        </div>

        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Disposition *
          </label>
          <select
            value={reviewData.disposition}
            onChange={(e) => setReviewData({...reviewData, disposition: e.target.value})}
            style={{
              width: '100%',
              padding: '0.6rem',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.9rem'
            }}
          >
            {DISPOSITIONS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Analyst Feedback
          </label>
          <textarea
            value={reviewData.analyst_feedback}
            onChange={(e) => setReviewData({...reviewData, analyst_feedback: e.target.value})}
            placeholder="Comments on the analyst's case handling..."
            style={{
              width: '100%',
              padding: '0.6rem',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.9rem',
              minHeight: '80px',
              resize: 'vertical'
            }}
          />
        </div>

        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Reviewer Feedback & Recommendations
          </label>
          <textarea
            value={reviewData.reviewer_feedback}
            onChange={(e) => setReviewData({...reviewData, reviewer_feedback: e.target.value})}
            placeholder="Your QA review findings and recommendations..."
            style={{
              width: '100%',
              padding: '0.6rem',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.9rem',
              minHeight: '100px',
              resize: 'vertical'
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onSubmit}
          style={{
            padding: '0.6rem 1.2rem',
            background: C.accent,
            color: '#0A0E1A',
            border: 'none',
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem'
          }}
        >
          Submit Review
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '0.6rem 1.2rem',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.textMid,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function QAReviewDetail({ qa, review, onClose, company, currentUser, team }) {
  const C = useTheme();
  const caseData = review?.caseDetails;

  const getDispositionLabel = (disposition) => {
    const disp = DISPOSITIONS.find(d => d.value === disposition);
    return disp ? disp.label : disposition;
  };

  const getDispositionColor = (disposition) => {
    const disp = DISPOSITIONS.find(d => d.value === disposition);
    return disp ? disp.color : '#999';
  };

  const scoringRubric = review?.scoring_rubric || {};
  const rubricItems = [
    { key: 'disposition', label: 'Disposition', weight: 0.20 },
    { key: 'conclusion', label: 'Conclusion', weight: 0.20 },
    { key: 'narrative_format', label: 'Narrative Format', weight: 0.20 },
    { key: 'reasonability', label: 'Reasonability', weight: 0.20 },
    { key: 'compliance', label: 'Compliance', weight: 0.20 },
  ];

  return (
    <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
            QA Review: {qa.case_id}
          </h3>
          {review?.created_at && (
            <div style={{ color: C.textDim, fontSize: '0.85rem' }}>
              {new Date(review.created_at).toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '0.4rem 0.8rem',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.textMid,
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          Close
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '2rem', minHeight: '70vh' }}>
        {/* Full Case Detail View */}
        {caseData && (
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            overflow: 'hidden',
            overflowY: 'auto'
          }}>
            <CaseDetail
              caseData={caseData}
              company={company}
              currentUser={currentUser}
              team={team}
              onBack={() => {}}
              onCaseUpdated={() => {}}
            />
          </div>
        )}

        {/* QA Review Panel */}
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '1.5rem',
          overflowY: 'auto'
        }}>
          <h4 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
            QA Assessment
          </h4>

          <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                OVERALL SCORE
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ color: C.accent, fontWeight: 700, fontSize: '2rem' }}>
                  {review.analyst_score || 0}%
                </div>
              </div>
            </div>

            <div>
              <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                DISPOSITION
              </label>
              <div style={{
                background: getDispositionColor(review.disposition) + '22',
                color: getDispositionColor(review.disposition),
                padding: '0.5rem 0.8rem',
                borderRadius: 4,
                display: 'inline-block',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}>
                {getDispositionLabel(review.disposition)}
              </div>
            </div>

            {/* Scoring Rubric */}
            <div>
              <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.75rem' }}>
                SCORE BREAKDOWN
              </label>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {rubricItems.map(item => {
                  const score = scoringRubric[item.key] || 0;
                  const points = Math.round(score * item.weight);
                  return (
                    <div key={item.key} style={{
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                      padding: '0.5rem',
                      fontSize: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span style={{ color: C.text, fontWeight: 600 }}>{item.label}</span>
                        <span style={{ color: C.accent, fontWeight: 700 }}>
                          {score}/100
                        </span>
                      </div>
                      <div style={{
                        background: C.border,
                        height: '3px',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: C.accent,
                          height: '100%',
                          width: `${score}%`,
                          transition: 'width 0.2s'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {review.analyst_feedback && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: C.textMid, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Analyst Feedback
              </label>
              <div style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 4,
                padding: '0.5rem',
                color: C.text,
                fontSize: '0.75rem',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {review.analyst_feedback}
              </div>
            </div>
          )}

          {review.reviewer_feedback && (
            <div>
              <label style={{ color: C.textMid, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Reviewer Feedback
              </label>
              <div style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 4,
                padding: '0.5rem',
                color: C.text,
                fontSize: '0.75rem',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {review.reviewer_feedback}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QAConfigPanel({ company, config, onUpdate }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(config || {
    sampling_percentage: 10,
    enabled: true,
    auto_route: true,
    sample_frequency: 'on_close',
  });

  async function handleSave() {
    try {
      if (config?.id) {
        const { error } = await db
          .from('qa_sampling_config')
          .update(formData)
          .eq('id', config.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('qa_sampling_config')
          .insert([{ ...formData, company_id: company.id }]);
        if (error) throw error;
      }
      showToast('QA configuration saved', 'success');
      setEditing(false);
      onUpdate();
    } catch (err) {
      showToast('Error saving configuration', 'error');
      console.error(err);
    }
  }

  if (!editing) {
    return (
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif" }}>QA Sampling Configuration</h3>
          <button
            onClick={() => setEditing(true)}
            style={{
              padding: '0.4rem 0.8rem',
              background: `${C.accent}22`,
              color: C.accent,
              border: `1px solid ${C.accent}`,
              borderRadius: 4,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.75rem',
              fontWeight: 700
            }}
          >
            Edit
          </button>
        </div>

        {config ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
            {[
              { label: 'Sampling %', value: config.sampling_percentage + '%' },
              { label: 'Enabled', value: config.enabled ? '✓ Yes' : '✗ No' },
              { label: 'Auto-Route', value: config.auto_route ? '✓ Yes' : '✗ No' },
              { label: 'Frequency', value: config.sample_frequency },
            ].map(item => (
              <div key={item.label} style={{ padding: '0.75rem', background: C.bg, borderRadius: 4 }}>
                <div style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {item.label}
                </div>
                <div style={{ color: C.text, fontWeight: 600 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: C.textDim, fontSize: '0.9rem' }}>
            No configuration set. Click "Edit" to configure QA sampling.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '1.5rem'
    }}>
      <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
        Configure QA Sampling
      </h3>

      <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Sampling Percentage (% of closed cases)
          </label>
          <input
            type="number"
            value={formData.sampling_percentage}
            onChange={(e) => setFormData({...formData, sampling_percentage: parseInt(e.target.value)})}
            min={1}
            max={100}
            style={{
              width: '100%',
              padding: '0.6rem',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.9rem'
            }}
          />
        </div>

        <div>
          <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Sample Frequency
          </label>
          <select
            value={formData.sample_frequency}
            onChange={(e) => setFormData({...formData, sample_frequency: e.target.value})}
            style={{
              width: '100%',
              padding: '0.6rem',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.9rem'
            }}
          >
            <option value="on_close">On Case Close</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '2rem' }}>
          <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.9rem' }}>Enabled</span>
          </label>
          <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.auto_route}
              onChange={(e) => setFormData({...formData, auto_route: e.target.checked})}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.9rem' }}>Auto-Route to QA</span>
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          style={{
            padding: '0.6rem 1.2rem',
            background: C.accent,
            color: '#0A0E1A',
            border: 'none',
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem'
          }}
        >
          Save Configuration
        </button>
        <button
          onClick={() => setEditing(false)}
          style={{
            padding: '0.6rem 1.2rem',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.textMid,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
